import { ARCHIVED_EXERCISE_MATERIALS } from './course-materials/archived-exercise-materials';
import { PERSONAL_COURSE_MATERIALS } from './course-materials/personal-course-materials';
import { ORDERED_COLLECTION_MATERIALS } from './course-materials/ordered-collection-materials';
import { enrichExercise } from './exercise-tag-inference';
import { buildCanonicalExerciseLibrary } from './library/canonical-exercise-library';
import { getLibraryReferenceBaseId } from '@/lib/libraryReferenceMapping';

// EXPORTS: IExercise, MOCK_EXERCISES, BodyPart, EquipmentType, SubCategory, SUBCATEGORY_LABELS, getExercisesByBodyPart, getExerciseById, MUSCLE_GROUPS, getMuscleGroupOfExercise, getMuscleGroupCount, getMuscleGroupLabel, getExercisesByMuscleGroup, MuscleGroup, DifficultyLevel

export type BodyPart =
  | 'chest' | 'back' | 'shoulders' | 'legs' | 'arms' | 'abs'
  | 'warmup' | 'stretch' | 'calves';

export type MuscleGroup = 'chest' | 'back' | 'shoulders' | 'arms' | 'legs' | 'abs' | 'warmup' | 'stretch';

export interface IMuscleGroupMeta {
  key: MuscleGroup;
  label: string;
  bodyParts: BodyPart[];
}

/** 8 大部位模块（胸/背/肩/手臂/腿/核心/激活/放松拉伸） */
export const MUSCLE_GROUPS: IMuscleGroupMeta[] = [
  { key: 'chest', label: '胸部', bodyParts: ['chest'] },
  { key: 'back', label: '背部', bodyParts: ['back'] },
  { key: 'shoulders', label: '肩部', bodyParts: ['shoulders'] },
  { key: 'arms', label: '手臂', bodyParts: ['arms'] },
  { key: 'legs', label: '腿部', bodyParts: ['legs', 'calves'] },
  { key: 'abs', label: '腹部核心', bodyParts: ['abs'] },
  { key: 'warmup', label: '激活', bodyParts: ['warmup'] },
  { key: 'stretch', label: '放松拉伸', bodyParts: ['stretch'] },
];

/** 判定是否为激活/热身类动作（按名称关键词） */
function isActivationExercise(ex: IExercise): boolean {
  if (ex.isWarmup) return true;
  if (ex.bodyPart === 'warmup') return true;
  const name = ex.name;
  // 明确的力量训练关键词（命中则肯定不是激活）
  const strengthKeywords = [
    '卧推', '划船', '弯举', '深蹲', '硬拉', '推举', '飞鸟', '侧平举', '前平举',
    '臂屈伸', '下拉', '夹胸', '保加利亚', '推肩', '实力推', '仰卧臂屈伸',
    '髋内收', '内收肌', '仰卧髋', '侧卧髋',
  ];
  for (const kw of strengthKeywords) {
    if (name.includes(kw)) return false;
  }
  // 名称关键词判定（仅热身/激活类）
  const activationKeywords = [
    '激活', '热身', '准备', '启动', '预激活', '肩袖', '手腕', '脚踝',
    '绕肩', '侧向移动弹力带', '蜘蛛式热身', '下斜方激活',
    '肩胛骨激活', '胸椎屈曲激活', '胸椎本体感受激活', 'Trx激活',
    '外旋肌激活', '内旋肌激活', '手肘搭平板凳', '头枕地对抗',
    '臀中肌臀小肌激活', '动态青蛙趴', '髋关节90/90', '屈髋抬腿',
    '靠墙单臂肩胛前引', '弹力带胸部激活', '弹力带肩部热身', '弹力带绕肩',
    '训练前准备', '肩袖激活',
  ];
  for (const kw of activationKeywords) {
    if (name.includes(kw)) return true;
  }
  // 泡沫轴松解类归入放松拉伸，不归激活
  if (name.includes('泡沫轴') || name.includes('松解') || name.includes('放松')) return false;
  // Y字系列：仅「俯卧Y字举」「下斜方激活Y字」是激活；「Y字侧平举」是力量训练（已被侧平举排除）
  if (name === '俯卧Y字举' || name.includes('下斜方激活') || name.includes('下斜方Y字')) return true;
  return false;
}

/** 判定是否为放松/拉伸类动作（按名称关键词） */
function isRelaxationExercise(ex: IExercise): boolean {
  if (ex.isStretch) return true;
  if (ex.bodyPart === 'stretch') return true;
  const relaxKeywords = [
    '拉伸', '放松', '松解', '泡沫轴', '筋膜', '侧弓步动态', '世界最伟大',
    '肩胛提肌', '胸锁乳突肌', '斜角肌', '颈阔肌', '拉伸对抗',
  ];
  const name = ex.name;
  for (const kw of relaxKeywords) {
    if (name.includes(kw)) return true;
  }
  // subCategory 是 chest-stretch / back-stretch 等
  if (ex.subCategory?.includes('stretch')) return true;
  return false;
}

/** 二次分类：bodyPart → 8大部位模块
 *  优先按动作性质（激活/放松拉伸）归位，再按 bodyPart 归到 6 大训练部位 */
export function getMuscleGroupOfExercise(ex: IExercise): MuscleGroup {
  if (ex.libraryGroup) return ex.libraryGroup;
  // 优先判定：放松/拉伸类 → 放松拉伸库
  if (isRelaxationExercise(ex)) return 'stretch';
  // 优先判定：激活/热身类 → 激活库
  if (isActivationExercise(ex)) return 'warmup';
  // 按 bodyPart 归位到 6 大训练部位
  const bp = ex.bodyPart;
  if (bp === 'chest') return 'chest';
  if (bp === 'back') return 'back';
  if (bp === 'shoulders') return 'shoulders';
  if (bp === 'arms') return 'arms';
  if (bp === 'legs' || bp === 'calves') return 'legs';
  if (bp === 'abs') return 'abs';
  return 'abs';
}

export function getMuscleGroupCount(groupKey: MuscleGroup, allExercises: IExercise[]): number {
  let count = 0;
  for (const ex of allExercises) {
    if (getMuscleGroupOfExercise(ex) === groupKey) count++;
  }
  return count;
}

export function getExercisesByMuscleGroup(groupKey: MuscleGroup, allExercises: IExercise[]): IExercise[] {
  return allExercises.filter(ex => getMuscleGroupOfExercise(ex) === groupKey);
}

export function getMuscleGroupLabel(groupKey: MuscleGroup): string {
  return MUSCLE_GROUPS.find(g => g.key === groupKey)?.label ?? '';
}

export type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';

export type EquipmentType =
  | 'barbell' | 'dumbbell' | 'machine' | 'bodyweight'
  | 'cable' | 'kettlebell' | 'foam-roller' | 'resistance-band'
  | 'trx' | 'ab-wheel';

// 细分分类 key（每个大部位下有若干细分）
export type SubCategory = string;

 // 全局 subCategory 兜底映射（跨 bodyPart 共享的英文细分名）
export const GLOBAL_SUBCATEGORY_LABELS: Record<string, string> = {
  // 胸
  'upper-chest': '上胸',
  'mid-chest': '中胸',
  'lower-chest': '下胸',
  'inner-chest': '中缝',
  // 背
  'width': '宽度',
  'thickness': '厚度',
  'lat': '背阔肌',
  'lats': '背阔肌',
  'row': '划船类',
  'rear-delt': '后束',
  'rear-deltoid': '后束',
  'mid-back': '中背',
  // 肩
  'front-deltoid': '前束',
  'side-deltoid': '中束',
  'medial-deltoid': '中束',
  'medial-delt': '中束',
  'anterior-delt': '前束',
  // 腿
  'quadriceps': '股四头肌',
  'quads': '股四头肌',
  'hamstrings': '腘绳肌',
  'glutes': '臀部',
  'calves': '小腿',
  'hamstrings-glutes': '腘绳+臀',
  'quads-glutes': '股四+臀',
  'lower-back': '下背',
  'hip-flexor': '髋屈肌',
  'inner-thigh': '大腿内侧',
  // 手臂
  'biceps': '二头肌',
  'triceps': '三头肌',
  // 腹
  'upper-abs': '上腹',
  'lower-abs': '下腹',
  'obliques': '侧腹',
  'core': '核心',
  'posture': '体态改善',
  // 小腿
  'gastrocnemius': '腓肠肌',
  'soleus': '比目鱼肌',
  // 热身
  'full-body': '全身激活',
  'shoulder': '肩部激活',
  'hip-leg': '髋腿激活',
  'thoracic': '胸椎/核心激活',
  'foam-roller': '泡沫轴放松',
  'chest': '胸部激活',
  'general': '综合热身',
  'abs': '腹部激活',
  'arms': '手臂激活',
  'back': '背部激活',
  'legs': '腿部激活',
  'neck': '颈部激活',
  // 拉伸
  'chest-stretch': '胸部拉伸',
  'back-stretch': '背部拉伸',
  'shoulder-stretch': '肩部拉伸',
  'arm-stretch': '手臂拉伸',
  'leg-stretch': '腿部拉伸',
  'abs-stretch': '腹部拉伸',
  'full-stretch': '全身拉伸',
};

/** 根据 bodyPart + subCategory 获取中文标签，优先部位内映射，兜底用全局映射 */
export function getSubCategoryLabel(bodyPart: BodyPart, subCategory: string): string {
  const partMap = SUBCATEGORY_LABELS[bodyPart];
  if (partMap && partMap[subCategory]) return partMap[subCategory];
  if (GLOBAL_SUBCATEGORY_LABELS[subCategory]) return GLOBAL_SUBCATEGORY_LABELS[subCategory];
  // 已经是中文则直接返回
  if (/[\u4e00-\u9fa5]/.test(subCategory)) return subCategory;
  return subCategory;
}
export const SUBCATEGORY_LABELS: Record<BodyPart, Record<SubCategory, string>> = {
  chest: {
    'upper-chest': '上胸',
    'mid-chest': '中胸',
    'lower-chest': '下胸',
    'inner-chest': '中缝',
  },
   back: {
     'width': '宽度',
     'thickness': '厚度',
     'lat': '背阔肌',
     'row': '划船类',
     'rear-delt': '后束（背部）',
     'lats': '背阔肌',
     'mid-back': '中背',
   },
   shoulders: {
     'front-deltoid': '前束',
     'side-deltoid': '中束',
     'rear-deltoid': '后束',
     'medial-deltoid': '中束',
     'medial-delt': '中束',
     'anterior-delt': '前束',
   },
   legs: {
     'quadriceps': '股四头',
     'hamstrings': '腘绳肌',
     'glutes': '臀部',
     'calves': '小腿',
     'quads': '股四头',
     'hamstrings-glutes': '腘绳+臀',
     'quads-glutes': '股四+臀',
     'lower-back': '下背',
     'hip-flexor': '髋屈肌',
     'inner-thigh': '大腿内侧',
   },
  arms: {
    'biceps': '二头肌',
    'triceps': '三头肌',
  },
   abs: {
     'upper-abs': '上腹',
     'lower-abs': '下腹',
     'obliques': '侧腹（核心）',
     'posture': '体态改善',
     'core': '综合',
   },
   warmup: {
     'full-body': '全身激活',
     'shoulder': '肩部激活',
     'hip-leg': '髋腿激活',
     'thoracic': '胸椎/核心激活',
     'foam-roller': '泡沫轴放松',
     'chest': '胸部激活',
     'core': '核心激活',
     'general': '综合热身',
     '放松': '放松',
     '激活': '激活',
     '拉伸': '拉伸',
     '髋': '髋部激活',
     '松解': '泡沫轴松解',
   },
   stretch: {
     'chest-stretch': '胸部拉伸',
     'back-stretch': '背部拉伸',
     'shoulder-stretch': '肩部拉伸',
     'arm-stretch': '手臂拉伸',
     'leg-stretch': '腿部拉伸',
     'abs-stretch': '腹部拉伸',
     'shoulder': '肩部拉伸',
     'neck': '颈部拉伸',
     'full-stretch': '全身拉伸',
   },
  calves: {
    gastrocnemius: '腓肠肌',
    soleus: '比目鱼肌',
  },
};

export const DIFFICULTY_LABELS: Record<DifficultyLevel, string> = {
  beginner: '入门',
  intermediate: '中级',
  advanced: '高级',
};

export interface IExercise {
  id: string;
  name: string;
  bodyPart: BodyPart;
  equipment: EquipmentType;
  /** 细分分类（动作库按细分分组展示用） */
  subCategory: SubCategory;
  tips: string[];
  sets: number;
  reps: string;
  rpe: number;
  restSeconds: number;
  isBodyweight: boolean;
  isStretch?: boolean;
  isWarmup?: boolean;
  /** 难度等级：入门 / 中级 / 高级 */
  difficulty?: DifficultyLevel;
  /** 主要目标肌群（中文标签数组） */
  targetMuscles?: string[];
  premium?: boolean;
  /** 精讲要点（仅部分动作有，用于动作详情页展示） */
  detailGuide?: string[];
  /** 动作来源版本号，有则在详情页标注 */
  source?: string;
  /** 示范图状态：'ready' 已就绪 / 'pending' 待更新，缺省时视为 ready */
  demoStatus?: 'ready' | 'pending';
  /** 动作演示视频地址（瘦身 MP4） */
  demoUrl?: string;
  /** 完整教学视频地址（长视频，预留；有值时显示播放角标） */
  videoUrl?: string;
  /** Uploaded course material with unresolved content mismatch; never falls back to an old teaching. */
  mediaIssue?: string;
  /** Explicit classification and reviewed aliases belong only to standard library entries. */
  libraryGroup?: MuscleGroup;
  movementPattern?: string;
  aliases?: string[];
}


/** Resource resolver includes independent uploaded teachings and retained historical references. */
const teachingMaterials = [...ARCHIVED_EXERCISE_MATERIALS, ...PERSONAL_COURSE_MATERIALS, ...ORDERED_COLLECTION_MATERIALS];
const canonicalLibrary = buildCanonicalExerciseLibrary(teachingMaterials);
export const MOCK_EXERCISES: IExercise[] = [...teachingMaterials, ...canonicalLibrary];
let enrichedExercises: IExercise[] | null = null;
function getAllExercisesEnriched(): IExercise[] {
  return enrichedExercises ??= canonicalLibrary.map(enrichExercise);
}

let resolvedMaterials: Map<string, IExercise> | null = null;

export function getExercisesByBodyPart(part: BodyPart): IExercise[] {
  return getAllExercisesEnriched().filter(exercise => exercise.bodyPart === part);
}

export function getExerciseById(id: string): IExercise | undefined {
  resolvedMaterials ??= new Map(MOCK_EXERCISES.map(exercise => [exercise.id, enrichExercise(exercise)]));
  const exact = resolvedMaterials.get(id);
  if (exact) return exact;
  const base = getLibraryReferenceBaseId(id);
  const standard = base !== id ? resolvedMaterials.get(base) : undefined;
  if (!standard?.libraryGroup) return undefined;
  const slot = { ...standard, id };
  resolvedMaterials.set(id, slot);
  return slot;
}

export function getAllExercises(): IExercise[] {
  return getAllExercisesEnriched();
}
